import { updateState } from '@angular-architects/ngrx-toolkit';
import { computed, DestroyRef, inject, Signal } from '@angular/core';
import {
    IComposedEmail,
    IEmail,
    IEmailConversationInfoEvent,
    IFileEntry,
    IWorkItem,
} from '@dxp/core/models';
import {
    signalStore,
    withFeature,
    withHooks,
    withMethods,
    withProps,
    withState,
} from '@ngrx/signals';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import {
    catchError,
    concatMap,
    distinct,
    filter,
    finalize,
    from,
    groupBy,
    ignoreElements,
    map,
    merge,
    mergeMap,
    Observable,
    of,
    switchMap,
    take,
    tap,
    timer,
    toArray,
} from 'rxjs';
import { environment } from '@dxp/core/environment';
import { EMAIL_SIGNALR_HUB } from '@dxp/core/signalr';
import { ContentApiService } from '@dxp/core/api';
import { UploadService } from '@dxp/core/services';
import { withWorkItems } from '../../workitems/workitems.feature';
import { WORKITEMS_STORE } from '../../workitems/workitems.token';
import { IEmailConversationStore, IEmailView } from './email.models';
import {
    areMessagesEquivalent,
    dedupeById,
    hasConversationInfoChanged,
    mapToEmail,
    sortByCreationDesc,
} from './email.helpers';

const DELAY_CONNECT_TIME = 500;
const BODY_CONCURRENCY = 5;

interface IEmailStoreState {
    conversations: Record<string, IEmailConversationStore>;
    views: Record<string, IEmailView>;
    composed: Record<string, IComposedEmail>;
    attachments: Record<string, IFileEntry[]>;
}

const initialState: IEmailStoreState = {
    conversations: {},
    views: {},
    composed: {},
    attachments: {},
};

type WorkItemEvent =
    | { kind: 'stateChanged'; workItem: IWorkItem }
    | { kind: 'removed'; workItem: IWorkItem };

export const EmailStore = signalStore(
    { providedIn: 'root' },
    environment.storeWithDevTools('EmailStore'),
    withFeature(() => withWorkItems(inject(WORKITEMS_STORE))),
    withState<IEmailStoreState>(initialState),
    withProps(store => {
        const conversations$ = toObservable(store.conversations);

        const waitForConversation$ = (
            conversationId: string,
        ): Observable<IEmailConversationStore> => {
            return conversations$.pipe(
                map(records => records[conversationId]),
                filter(
                    (conversation): conversation is IEmailConversationStore =>
                        conversation !== undefined,
                ),
                take(1),
            );
        };

        const emailRemoved$ = merge(
            store.onWorkItemStateChanged$.pipe(
                map(workItem => ({ kind: 'stateChanged', workItem }) as const),
            ),
            store.onWorkItemRemoved$.pipe(
                map(workItem => ({ kind: 'removed', workItem }) as const),
            ),
        ).pipe(
            filter(e => e.workItem.primaryMediaType === 'Email'),
            filter(
                e =>
                    e.kind !== 'stateChanged' ||
                    e.workItem.workItemState === 'Afterwork',
            ),
        );

        const conversationById = computed(() => store.conversations());

        const conversationSelectorCache = new Map<
            string,
            Signal<IEmailConversationStore | undefined>
        >();

        const selectConversation = (conversationId: string) => {
            const existing = conversationSelectorCache.get(conversationId);
            if (existing) return existing;

            const sel = computed(() => conversationById()[conversationId]);
            conversationSelectorCache.set(conversationId, sel);
            return sel;
        };

        const composedById = computed(() => store.composed());

        const composedSelectorCache = new Map<
            string,
            Signal<IComposedEmail | undefined>
        >();

        const selectComposed = (conversationId: string) => {
            const existing = composedSelectorCache.get(conversationId);
            if (existing) return existing;

            const sel = computed(() => composedById()[conversationId]);
            composedSelectorCache.set(conversationId, sel);
            return sel;
        };

        const attachmentsById = computed(() => store.attachments());

        const attachmentsSelectorCache = new Map<
            string,
            Signal<IFileEntry[] | undefined>
        >();

        const selectAttachments = (conversationId: string) => {
            const existing = attachmentsSelectorCache.get(conversationId);
            if (existing) return existing;

            const sel = computed(() => attachmentsById()[conversationId]);
            attachmentsSelectorCache.set(conversationId, sel);
            return sel;
        };

        const viewById = computed(() => store.views());

        const viewSelectorCache = new Map<
            string,
            Signal<IEmailView | undefined>
        >();

        const selectView = (conversationId: string) => {
            const existing = viewSelectorCache.get(conversationId);
            if (existing) return existing;

            const sel = computed(() => viewById()[conversationId]);
            viewSelectorCache.set(conversationId, sel);
            return sel;
        };

        const purgeSelectors = (conversationId: string) => {
            conversationSelectorCache.delete(conversationId);
            composedSelectorCache.delete(conversationId);
            attachmentsSelectorCache.delete(conversationId);
            viewSelectorCache.delete(conversationId);
        };

        return {
            selectConversation: selectConversation,
            selectComposed: selectComposed,
            selectAttachments: selectAttachments,
            selectView: selectView,
            purgeSelectors: purgeSelectors,
            waitForConversation$: waitForConversation$,
            emailRemoved$: emailRemoved$,
        };
    }),
    withMethods(store => ({
        getConversation: store.selectConversation,
        getComposed: store.selectComposed,
        getAttachments: store.selectAttachments,
        getView: store.selectView,
        updateComposed(conversationId: string, composed: IComposedEmail) {
            updateState(store, 'UPDATE_EMAIL_COMPOSED', state => ({
                ...state,
                composed: {
                    ...state.composed,
                    [conversationId]: composed,
                },
            }));
        },
        updateAttachments(conversationId: string, attachments: IFileEntry[]) {
            updateState(store, 'UPDATE_EMAIL_ATTACHMENTS', state => ({
                ...state,
                attachments: {
                    ...state.attachments,
                    [conversationId]: attachments,
                },
            }));
        },
        updateView(conversationId: string, view: IEmailView) {
            updateState(store, 'UPDATE_EMAIL_VIEW', state => ({
                ...state,
                views: {
                    ...state.views,
                    [conversationId]: view,
                },
            }));
        },
        updateConversationInfo(conversationInfo: IEmailConversationInfoEvent) {
            const id = conversationInfo.conversationId;

            updateState(store, 'UPSERT_EMAIL_CONVERSATION_INFO', state => {
                const existing = state.conversations[id];

                const nextInfo: IEmailConversationInfoEvent = {
                    ...conversationInfo,
                };

                if (!existing) {
                    return {
                        ...state,
                        conversations: {
                            ...state.conversations,
                            [id]: {
                                information: nextInfo,
                                messages: [],
                            },
                        },
                    };
                }

                if (!existing.information) {
                    return {
                        ...state,
                        conversations: {
                            ...state.conversations,
                            [id]: {
                                ...existing,
                                information: nextInfo,
                            },
                        },
                    };
                }

                if (
                    !hasConversationInfoChanged(existing.information, nextInfo)
                ) {
                    return state;
                }

                return {
                    ...state,
                    conversations: {
                        ...state.conversations,
                        [id]: {
                            ...existing,
                            information: nextInfo,
                        },
                    },
                };
            });
        },
        closeConversation(conversationId: string) {
            const hasAny =
                store.conversations()[conversationId] !== undefined ||
                store.composed()[conversationId] !== undefined ||
                store.attachments()[conversationId] !== undefined ||
                store.views()[conversationId] !== undefined;

            if (!hasAny) return;

            store.purgeSelectors(conversationId);

            updateState(store, 'REMOVE_EMAIL_CONVERSATION', state => {
                const { [conversationId]: _, ...conversations } =
                    state.conversations;
                const { [conversationId]: __, ...composed } = state.composed;
                const { [conversationId]: ___, ...attachments } =
                    state.attachments;
                const { [conversationId]: ____, ...views } = state.views;

                return {
                    ...state,
                    conversations,
                    composed,
                    attachments,
                    views,
                };
            });
        },

        updateEmailMessages(conversationId: string, messages: IEmail[]) {
            updateState(store, 'UPDATE_EMAIL_MESSAGES', state => {
                const existing = state.conversations[conversationId];
                if (!existing) return state;

                if (areMessagesEquivalent(existing.messages, messages)) {
                    return state;
                }

                return {
                    ...state,
                    conversations: {
                        ...state.conversations,
                        [conversationId]: {
                            ...existing,
                            messages,
                        },
                    },
                };
            });
        },
    })),
    withMethods(store => ({
        addEmailMessage(message: IEmail) {
            const existing = store.conversations()[message.conversationId];
            const combined = dedupeById([
                ...(existing?.messages ?? []),
                message,
            ]);

            const sorted = sortByCreationDesc(combined);

            store.updateEmailMessages(message.conversationId, sorted);
        },
    })),
    withHooks({
        onInit(
            store,
            emailHub = inject(EMAIL_SIGNALR_HUB),
            contentApiService = inject(ContentApiService),
            uploadService = inject(UploadService),
            destroyRef = inject(DestroyRef),
        ) {
            store.onWorkItemAdded$
                .pipe(
                    filter(
                        workitem =>
                            (workitem.workItemState === 'Preview' ||
                                workitem.workItemState === 'Connected') &&
                            workitem.primaryMediaType === 'Email',
                    ),
                    switchMap(workItem =>
                        timer(DELAY_CONNECT_TIME).pipe(
                            tap(() => {
                                emailHub.peekOrConnect(
                                    workItem.primaryConversationId,
                                );
                            }),
                        ),
                    ),
                    takeUntilDestroyed(destroyRef),
                )
                .subscribe();

            store.emailRemoved$
                .pipe(
                    distinct(e => e.workItem.primaryConversationId),
                    concatMap((e: WorkItemEvent) => {
                        const workItem = e.workItem;

                        const attachments =
                            store.attachments()[
                                workItem.primaryConversationId
                            ] ?? [];

                        const toRemove = attachments.filter(
                            r => r.state === 'uploaded' && r.response?.success,
                        );

                        const remove$ =
                            toRemove.length > 0
                                ? from(toRemove).pipe(
                                      concatMap(file => {
                                          const path =
                                              file.response?.data.files[0] ??
                                              '';
                                          const { directory, filename } =
                                              uploadService.splitDirAndFile(
                                                  path,
                                              );

                                          return uploadService
                                              .removeJoditFile(
                                                  directory,
                                                  filename,
                                              )
                                              .pipe(
                                                  take(1),
                                                  catchError(() => of(void 0)),
                                              );
                                      }),
                                  )
                                : of(void 0);

                        return remove$.pipe(
                            finalize(() => {
                                store.closeConversation(
                                    workItem.primaryConversationId,
                                );
                            }),
                        );
                    }),
                    takeUntilDestroyed(destroyRef),
                )
                .subscribe();

            emailHub.conversationInfo$
                .pipe(
                    tap(conversationInfo => {
                        store.updateConversationInfo(conversationInfo);
                    }),

                    groupBy(
                        conversationInfo => conversationInfo.conversationId,
                    ),

                    mergeMap(group$ => {
                        const loadOnce$ = group$.pipe(
                            take(1),
                            switchMap(conversationInfo => {
                                const conversationId =
                                    conversationInfo.conversationId;
                                const threadId =
                                    conversationInfo.conversation.threadId;

                                return contentApiService
                                    .getEmailMessages(conversationId, threadId)
                                    .pipe(
                                        switchMap(emails => {
                                            if (!emails?.length)
                                                return of([] as IEmail[]);

                                            return from(emails).pipe(
                                                mergeMap(
                                                    email =>
                                                        contentApiService
                                                            .getEmailBody(
                                                                email.bodyId,
                                                            )
                                                            .pipe(
                                                                map(
                                                                    body =>
                                                                        ({
                                                                            ...email,
                                                                            body,
                                                                        }) as IEmail,
                                                                ),
                                                                catchError(() =>
                                                                    of({
                                                                        ...email,
                                                                        body: undefined,
                                                                    } as IEmail),
                                                                ),
                                                            ),
                                                    BODY_CONCURRENCY,
                                                ),
                                                toArray(),
                                            );
                                        }),
                                        map(emails =>
                                            sortByCreationDesc(emails),
                                        ),
                                        tap(sorted => {
                                            store.updateEmailMessages(
                                                conversationId,
                                                sorted,
                                            );
                                        }),
                                        catchError(() => of([] as IEmail[])),
                                    );
                            }),
                        );

                        const keepAlive$ = group$.pipe(ignoreElements());

                        return merge(loadOnce$, keepAlive$);
                    }),

                    takeUntilDestroyed(destroyRef),
                )
                .subscribe();

            emailHub.emailMessage$
                .pipe(
                    map(inbound => mapToEmail(inbound)),
                    mergeMap(email =>
                        contentApiService.getEmailBody(email.bodyId).pipe(
                            map(body => ({ ...email, body }) as IEmail),
                            catchError(() =>
                                of({ ...email, body: undefined } as IEmail),
                            ),
                        ),
                    ),
                    tap(emailMessage => {
                        const existing =
                            store.conversations()[emailMessage.conversationId];
                        const combined = dedupeById([
                            ...(existing?.messages ?? []),
                            emailMessage,
                        ]);

                        const sorted = sortByCreationDesc(combined);

                        store.updateEmailMessages(
                            emailMessage.conversationId,
                            sorted,
                        );
                    }),
                    takeUntilDestroyed(destroyRef),
                )
                .subscribe();
        },
    }),
);
